/**
 * BainScorerSection — three sliders for Bain adjacency dimensions.
 *
 * Each slider ranges 1–10 and shows a descriptive label from BAIN_SCORE_LABELS.
 * The composite relatedness score (weighted, same as the Priority Matrix) is
 * displayed below with its weighting.
 */

import { BAIN_SCORE_LABELS } from '../../types/adjacency';
import { relatednessScore, formatRelatedness, RELATEDNESS_WEIGHTING_LABEL } from '../../utils/relatedness';

interface BainScorerSectionProps {
  sharedCustomers: number;
  sharedCosts: number;
  commonCompetitors: number;
  onChange: (field: 'sharedCustomers' | 'sharedCosts' | 'commonCompetitors', value: number) => void;
}

const DIMENSIONS: { key: 'sharedCustomers' | 'sharedCosts' | 'commonCompetitors'; label: string }[] = [
  { key: 'sharedCustomers', label: 'Shared Customers' },
  { key: 'sharedCosts', label: 'Shared Costs' },
  { key: 'commonCompetitors', label: 'Common Competitors' },
];

export function BainScorerSection({ sharedCustomers, sharedCosts, commonCompetitors, onChange }: BainScorerSectionProps) {
  const values = { sharedCustomers, sharedCosts, commonCompetitors };
  // WQ-343 D1(a): the same weighted score the Priority Matrix plots
  const composite = formatRelatedness(relatednessScore(values));

  return (
    <div className="space-y-3">
      {DIMENSIONS.map(({ key, label }) => {
        const val = values[key];
        return (
          <div key={key}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>{label}</span>
              <span className="text-[11px] font-medium" style={{ color: 'var(--text-primary)' }}>
                {val}/10
                <span className="ml-1 text-[10px]" style={{ color: 'var(--text-muted)' }}>
                  ({BAIN_SCORE_LABELS[val] ?? '?'})
                </span>
              </span>
            </div>
            <input
              type="range"
              min={1}
              max={10}
              step={1}
              value={val}
              onChange={(e) => onChange(key, Number(e.target.value))}
              className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, #3b82f6 ${((val - 1) / 9) * 100}%, rgba(100,116,139,0.3) ${((val - 1) / 9) * 100}%)`,
              }}
            />
          </div>
        );
      })}

      {/* Composite score */}
      <div
        className="flex items-center justify-between px-3 py-2 rounded-lg"
        style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}
      >
        <span className="flex flex-col">
          <span className="text-[11px] font-semibold" style={{ color: 'var(--text-secondary)' }}>
            Relatedness Score
          </span>
          <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
            {RELATEDNESS_WEIGHTING_LABEL}
          </span>
        </span>
        <span className="text-[13px] font-bold" style={{ color: '#3b82f6' }}>
          {composite}
          <span className="text-[10px] font-normal" style={{ color: 'var(--text-muted)' }}>/10</span>
        </span>
      </div>
    </div>
  );
}
